import { Component, inject, input, output, TemplateRef } from '@angular/core';
import { GetPayterTerminalLocationsRequest, PtsPaymentTerminalService } from '../../services/payment-terminal.service';
import { TypeAheadSelector2Component } from '../type-ahead-selector2/type-ahead-selector2.component';
import { PayterTerminalLocation } from 'src/app/features/payment-terminal/pts/pts-payment-terminal-model';
import { debounceTime, map, Observable, of, switchMap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AddressService } from 'src/app/core/services/address.service';

@Component({
  selector: 'adm-admin-payter-locations-type-ahead',
  templateUrl: './admin-payter-locations-type-ahead.component.html',
  styleUrl: './admin-payter-locations-type-ahead.component.scss',
  imports: [TypeAheadSelector2Component, MatIconModule, MatTooltipModule],
})
export class AdminPayterLocationsTypeAheadComponent {
  ptsService = inject(PtsPaymentTerminalService);
  addressService = inject(AddressService);
  itemSelected = output<PayterTerminalLocation | null>();
  initialId = input<string>();
  filterByProp = input<string>('id');
  itemTemplate = input<TemplateRef<any> | null>(null);
  resetTrigger = input<{ reset: boolean }>();
  //getPayterTerminalLocations returns all locations which have a terminal attached which means that we can get multiple location presented twice, this parameter will ensure that same location is only presented once
  unique = input<boolean>(false);
  clearTypeAhead = output();
  placeholder = input<string>('Lokation');
  fetchedLocations: PayterTerminalLocation[] | null = null;

  private locations$ = this.ptsService.getPayterTerminalLocations().pipe(
    map(locations => {
      if (this.fetchedLocations != null) {
        return this.fetchedLocations;
      }
      if (!this.unique()) {
        return locations.map(location => this.withFormattedAddress(location));
      } else {
        const uniqueLocations = locations.filter(
          (location, index, self) =>
            index === self.findIndex(l => l.chargingStationLocationIdentifier === location.chargingStationLocationIdentifier)
        );
        return uniqueLocations.map(location => this.withFormattedAddress(location));
      }
    })
  );

  private withFormattedAddress(location: PayterTerminalLocation): PayterTerminalLocation {
    location.formattedAddress = this.addressService.formatAddressToString(location.paymentTerminalAddress);
    return location;
  }

  fetchLocations = (term: string): Observable<PayterTerminalLocation[]> => {
    const query = { query: term } as GetPayterTerminalLocationsRequest;

    return of(term).pipe(
      debounceTime(300),
      switchMap(() => this.ptsService.getPayterTerminalLocations(query)),
      map(locations => locations.map(location => this.withFormattedAddress(location)))
    );
  };

  fetchLocationsById = (id: string): Observable<PayterTerminalLocation | null> => {
    return this.locations$.pipe(
      switchMap(locations => {
        if (locations) {
          const location = locations.find(loc => loc.chargingStationLocationIdentifier === id);
          if (location) {
            return of(location);
          }
        }
        return of(null);
      })
    );
  };

  locationSelected = (location: PayterTerminalLocation | null | undefined) => {
    this.itemSelected.emit(location ?? null);
  };
}
